export type Provedor = 'youtube' | 'vimeo' | 'panda' | 'bunny';
export const PROVEDORES: Provedor[] = ['youtube', 'vimeo', 'panda', 'bunny'];
export const PRECISA_BIBLIOTECA: Record<Provedor, boolean> = { youtube: false, vimeo: false, panda: true, bunny: true };

export interface ProgressoCurso {
  matricula_id: string;
  origem: 'manual' | 'trilha';
  atribuida_em: string;
  concluida_em: string | null;
  codigo_certificado: string | null;
  aulas_concluidas: number;
  percentual: number;
}

export interface Curso {
  id: string;
  titulo: string;
  descricao: string | null;
  capa_anexo_id: string | null;
  publicado: boolean;
  carga_horaria_min: number;
  duracao_min: number;
  pre_requisito_curso_id: string | null;
  pre_requisito_titulo: string | null;
  modulos: number;
  aulas: number;
  matriculados: number;
  minha: ProgressoCurso | null;
  bloqueado: boolean;
  created_at: string;
}

/** Fase 2 (W17, K1): a aula de texto não tem provedor nem vídeo; o texto é a `descricao`. */
export type TipoAula = 'video' | 'texto';

export interface AulaItem {
  id: string;
  titulo: string;
  ordem: number;
  tipo: TipoAula;
  /** Chave da aula do sistema (o id da novidade, no curso de novidades). */
  chave_sistema: string | null;
  provedor: Provedor | null;
  duracao_min: number;
  pre_requisito_aula_id: string | null;
  concluida: boolean;
  bloqueada: boolean;
  posicao_seg: number;
  materiais: number;
  provedor_disponivel: boolean;
  video_id?: string | null;
  biblioteca?: string | null;
}

export interface ModuloItem {
  id: string;
  titulo: string;
  ordem: number;
  aulas: AulaItem[];
}

export interface CursoFicha extends Curso {
  modulos_itens: ModuloItem[];
  eu: { id: string; nome: string } | null;
  proxima_aula_id: string | null;
  players: { habilitados: Provedor[]; conectado: boolean };
}

export interface Material {
  id: string;
  nome: string;
  mime: string;
  tamanho: number;
}

export interface AulaDetalhe {
  id: string;
  curso_id: string;
  curso_titulo: string;
  modulo_id: string;
  modulo_titulo: string;
  titulo: string;
  descricao: string | null;
  tipo: TipoAula;
  chave_sistema: string | null;
  provedor: Provedor | null;
  duracao_min: number;
  pre_requisito_aula_id: string | null;
  pre_requisito_titulo: string | null;
  concluida: boolean;
  posicao_seg: number;
  bloqueada: boolean;
  motivo_bloqueio: 'aula' | 'curso' | null;
  provedor_disponivel: boolean;
  embed_url: string | null;
  materiais: Material[];
  anterior_id: string | null;
  proxima_id: string | null;
  registra_progresso: boolean;
  video_id?: string | null;
  biblioteca?: string | null;
}

export interface Trilha {
  id: string;
  nome: string;
  descricao: string | null;
  funcao_id: string | null;
  funcao_nome: string | null;
  cursos: number;
  duracao_min: number;
  matriculas: number;
}

export interface TrilhaCurso {
  id: string;
  titulo: string;
  publicado: boolean;
  ordem: number;
  aulas: number;
  duracao_min: number;
  minha: { concluida_em: string | null } | null;
}

export interface TrilhaFicha extends Trilha {
  itens: TrilhaCurso[];
}

export interface Matricula {
  id: string;
  funcionario_id: string;
  funcionario_nome: string;
  funcao_nome: string | null;
  curso_id: string;
  curso_titulo: string;
  origem: 'manual' | 'trilha';
  trilha_id: string | null;
  trilha_nome: string | null;
  atribuida_em: string;
  concluida_em: string | null;
  codigo_certificado: string | null;
  aulas_total: number;
  aulas_concluidas: number;
  percentual: number;
}

export interface ProgressoPessoa {
  id: string;
  nome: string;
  funcao_nome: string | null;
  matriculas: number;
  cursos_concluidos: number;
  em_andamento: number;
  aulas_total: number;
  aulas_concluidas: number;
  percentual: number;
}

export interface ProgressoPorCurso {
  id: string;
  titulo: string;
  publicado: boolean;
  aulas: number;
  matriculados: number;
  concluidos: number;
  em_andamento: number;
  percentual_medio: number;
}

export interface Certificado {
  valido: true;
  codigo: string;
  funcionario_nome: string;
  funcao_nome: string | null;
  curso_id: string;
  curso_titulo: string;
  carga_horaria_min: number;
  concluida_em: string;
  emitido_em: string;
}

export interface Eu {
  funcionario: { id: string; nome: string; funcao_nome: string | null } | null;
  pode_editar: boolean;
}

/** Minutos como "1 h 30 min" ou "45 min". */
export function duracaoTexto(min: number): string {
  if (!min) return '0 min';
  const h = Math.floor(min / 60);
  const m = min % 60;
  return h ? (m ? `${h} h ${m} min` : `${h} h`) : `${m} min`;
}

/** Segundos como "m:ss" ou "h:mm:ss". */
export function relogio(seg: number): string {
  const h = Math.floor(seg / 3600);
  const m = Math.floor((seg % 3600) / 60);
  const s = Math.floor(seg % 60);
  const dois = (n: number) => String(n).padStart(2, '0');
  return h ? `${h}:${dois(m)}:${dois(s)}` : `${m}:${dois(s)}`;
}

/** Remove vazios para o corpo da requisição: '' vira null. */
export function limpar(v: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(v).map(([k, x]) => [k, x === '' ? null : x]));
}
